import { useTranslation, UseTranslationOptions } from "react-i18next";
import i18n from "../i18n";

async function retryFunction<T>(
	fn: (...args: unknown[]) => T | Promise<T>,
	delayInMs: number,
	retries?: number,
): Promise<T> {
	try {
		const val = await fn();
		return val;
	} catch (error) {
		console.error(error);
		if (retries === 0) {
			throw error;
		}
		await new Promise((res) => setTimeout(res, delayInMs));
		return await retryFunction(fn, delayInMs, retries ? retries - 1 : undefined);
	}
}

export type RetryPromise<T> = Promise<T>;

export type RetryOptions = {
	delayInMs?: number;
	maxRetries?: number;
};

/**
 *
 * @param fn
 * @param options
 * @param options.delayInMs defaults to 500 ms
 * @param options.maxRetries
 * @returns
 */
export function retry<T>(fn: (...args: unknown[]) => T | Promise<T>, options?: RetryOptions): RetryPromise<T> {
	return retryFunction(fn, options?.delayInMs ?? 500, options?.maxRetries);
}

export function sleep(ms: number): Promise<void> {
	return new Promise((resolve) => {
		setTimeout(resolve, ms);
	});
}

/**Asserts string so typescript doesn't keep complaining */
export function translate(key: string, options?: Record<string, unknown>): string {
	return i18n.t(key, options) as string;
}

/**Wrapper around `useTranslation` to assert string so we don't see yellow warnings */
export function useAppTranslation(options?: UseTranslationOptions<string>) {
	const { t: originalT, ...rest } = useTranslation(undefined, options);

	const t = (key: string, tOptions?: Record<string, unknown>): string => {
		return originalT(key, tOptions) as string;
	};

	// `<Trans t={...} />` needs the real TFunction
	return { ...rest, t, rawT: originalT };
}

export function deepEqual(x: unknown, y: unknown): boolean {
	// Primitive equality & same reference check
	if (x === y) return true;

	// If either is null/undefined or not an object, they aren't equal (since x !== y above)
	if (x === null || y === null || typeof x !== "object" || typeof y !== "object") {
		return false;
	}

	// Handle Date objects
	if (x instanceof Date && y instanceof Date) {
		return x.getTime() === y.getTime();
	}

	// Array
	if (Array.isArray(x) !== Array.isArray(y)) {
		return false;
	}

	// Compare Object keys length
	const xRecord = x as Record<string, unknown>;
	const yRecord = y as Record<string, unknown>;

	const xKeys = Object.keys(xRecord);
	const yKeys = Object.keys(yRecord);

	if (xKeys.length !== yKeys.length) return false;

	// Ensure y has every key from x and values are deeply equal
	return xKeys.every(
		(key) => Object.prototype.hasOwnProperty.call(yRecord, key) && deepEqual(xRecord[key], yRecord[key]),
	);
}

export function isNonEmptyString(value: unknown): value is string {
	return typeof value === "string" && value.trim().length > 0;
}
